/*eslint max-lines-per-function: ["error", 260]*/
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { sortBy } from 'lodash';
import { useSearchParams } from 'react-router-dom';

import {
  CreateUpdateSalesInvoicePositionDto,
  CreateUpdateSalesInvoicePositionDtoVatEnum,
  GetSalesInvoiceDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { SalesInvoiceEditorTabs } from '../../containers/SalesInvoiceEditorView/SalesInvoiceEditorView';
import {
  newSalesInvoicePosition,
  validatePosition,
} from '../../../common/spdCore/contexts/SalesInvoiceContext';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import useSelectGridRow from '../../../common/hooks/useSelectGridRow';
import mathUtils from '../../../utils/mathUtils';
import { getGrossPrice } from '../../../utils/getVatAmountByVat';
import { VatEnum } from '../../../common/spdCore/validation/enums';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';

import { SalesInvoicePositionsGrid } from './SalesInvoicePositionsGrid';
import { SalesInvoicePositionsControls } from './SalesInvoicePositionsControls';

interface SalesInvoicePositionsProps {
  readOnly?: boolean;
  salesInvoice?: GetSalesInvoiceDto;
  id?: string;
  handleRefresh: () => Promise<void>;
  setNotSavedTab: (tab: SalesInvoiceEditorTabs | undefined) => void;
  setActiveTab: Dispatch<SetStateAction<string>>;
  positionsRefreshKey?: boolean;
}

const sortPositions = (positions: CreateUpdateSalesInvoicePositionDto[]) => {
  const result = sortBy(positions, ['order'], ['asc']);
  return result.map((position, index) => ({ ...position, order: index + 1 }));
};

export const SalesInvoicePositions: React.FC<
  SalesInvoicePositionsProps
> = props => {
  const select = useSelectGridRow<number>();
  const spd = useSpdCore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [positions, setPositions] = useState<
    CreateUpdateSalesInvoicePositionDto[]
  >([]);

  const resetPositions = () => {
    const newPositions: CreateUpdateSalesInvoicePositionDto[] = [
      newSalesInvoicePosition(1, props.id || ''),
    ];
    setPositions(newPositions);
    props.setNotSavedTab(SalesInvoiceEditorTabs.POSITIONS);
  };

  const initializeEmptyPositions = () => {
    const newPositions: CreateUpdateSalesInvoicePositionDto[] = [
      newSalesInvoicePosition(1, props.id || ''),
    ];
    setPositions(newPositions);
  };

  useEffect(() => {
    if (
      props.salesInvoice?.salesInvoicePositions &&
      props.salesInvoice.salesInvoicePositions.length > 0
    ) {
      const sortedPositions = sortPositions(
        props.salesInvoice.salesInvoicePositions.map(p => ({
          name: p.name || '',
          amount: p.amount || '0',
          quantity: p.quantity ?? 1,
          vat: p.vat as unknown as CreateUpdateSalesInvoicePositionDtoVatEnum,
          order: p.order || 1,
          active: p.active,
          salesInvoiceId: props.id || '',
          isLocked: p.isLocked || false,
        })),
      );
      setPositions(sortedPositions);
    } else if (
      props.salesInvoice &&
      (!props.salesInvoice.salesInvoicePositions ||
        props.salesInvoice.salesInvoicePositions.length === 0) &&
      props.id
    ) {
      initializeEmptyPositions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.salesInvoice?.id,
    props.id,
    props.salesInvoice?.amountGross,
    props.positionsRefreshKey,
  ]);

  const propertyChange = (
    obj: Partial<CreateUpdateSalesInvoicePositionDto>,
    index: number,
  ) => {
    const newPositions = positions.map(position =>
      position.order === index ? { ...position, ...obj } : position,
    );
    setPositions(newPositions);
    props.setNotSavedTab(SalesInvoiceEditorTabs.POSITIONS);
  };

  const clonePosition = (key: number) => {
    const position = positions.find(p => p.order === key);
    if (!position) {
      return;
    }

    const newPosition = {
      ...position,
      id: undefined,
      order: positions.length + 1,
    };
    const newPositions = [...positions, newPosition];
    setPositions(sortPositions(newPositions));
    props.setNotSavedTab(SalesInvoiceEditorTabs.POSITIONS);
  };

  const addNewPosition = () => {
    const newPosition = newSalesInvoicePosition(
      positions.length + 1,
      props.id || '',
    );
    const newPositions = [...positions, newPosition];
    setPositions(sortPositions(newPositions));
    props.setNotSavedTab(SalesInvoiceEditorTabs.POSITIONS);
  };

  const deletePositions = (keys: number[]) => {
    const newPositions = positions.filter(p => !keys.includes(p.order));
    setPositions(sortPositions(newPositions));
    select.handleUnselectAll();
    props.setNotSavedTab(SalesInvoiceEditorTabs.POSITIONS);
  };

  const handleSave = async () => {
    if (props.readOnly) {
      props.setActiveTab(SalesInvoiceEditorTabs.FINALIZE);
      setSearchParams(searchParams, {
        state: {
          tabKey: SalesInvoiceEditorTabs.FINALIZE,
        },
      });
      return;
    }
    setIsSaveLoading(true);
    setSaveErrors([]);
    setShowSaveErrors(false);

    try {
      if (!props.id) {
        setSaveErrors(['Najpierw zapisz dane podstawowe faktury']);
        setShowSaveErrors(true);
        setIsSaveLoading(false);
        return;
      }

      // Filter out completely empty positions (no data at all)
      const nonEmptyPositions = positions.filter(
        p =>
          (p.name && p.name.trim() !== '') ||
          (p.amount && p.amount !== '0') ||
          p.vat !== CreateUpdateSalesInvoicePositionDtoVatEnum.zw,
      );

      // If no positions provided, show error
      if (nonEmptyPositions.length === 0) {
        setSaveErrors(['Proszę dodać co najmniej jedną pozycję faktury.']);
        setShowSaveErrors(true);
        setIsSaveLoading(false);
        return;
      }

      // Validate all non-empty positions
      const validationErrors = await validatePositions(
        nonEmptyPositions,
        !!props.salesInvoice?.originalInvoice?.id,
      );

      if (validationErrors.length > 0) {
        setSaveErrors(validationErrors);
        setShowSaveErrors(true);
        setIsSaveLoading(false);
        return;
      }

      // createMany handles deletion of old positions and creation of new ones
      await spd.salesInvoices.createPositions(
        nonEmptyPositions,
        props.id,
        props.salesInvoice?.ts,
      );

      props.setNotSavedTab(undefined);
      await props.handleRefresh();

      setSearchParams(searchParams, {
        state: {
          tabKey: SalesInvoiceEditorTabs.FINALIZE,
        },
      });
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setSaveErrors(errors);
      setShowSaveErrors(true);
    } finally {
      setIsSaveLoading(false);
    }
  };

  const parentPositions = useMemo(
    () =>
      props.salesInvoice?.originalInvoice?.id
        ? (props.salesInvoice.parent?.salesInvoicePositions ?? [])
        : undefined,

    [
      props.salesInvoice?.originalInvoice?.id,
      props.salesInvoice?.parent?.salesInvoicePositions,
    ],
  );

  const parentPositionsNetSum = useMemo(() => {
    if (!parentPositions) {
      return undefined;
    }
    return parentPositions
      .reduce((acc, pos) => {
        const qty = (pos.quantity ?? 1).toString();
        const total = mathUtils.multiply(pos.amount || '0', qty);
        return mathUtils.add(acc, total).toString();
      }, '0')
      .toString();
  }, [parentPositions]);

  const parentPositionsGrossSum = useMemo(() => {
    if (!parentPositions) {
      return undefined;
    }
    return parentPositions
      .reduce((acc, pos) => {
        const qty = pos.quantity ?? 1;
        const totalNet = mathUtils.multiply(pos.amount || '0', qty.toString());
        const gross = getGrossPrice(totalNet, pos.vat as unknown as VatEnum);
        return mathUtils.add(acc, gross).toString();
      }, '0')
      .toString();
  }, [parentPositions]);

  return (
    <div className="d-flex flex-column gap-3">
      <SalesInvoicePositionsGrid
        select={select}
        positions={positions}
        propertyChange={propertyChange}
        readOnly={props.readOnly}
        parentAmountNet={parentPositionsNetSum}
        parentAmountGross={parentPositionsGrossSum}
        isCorrection={!!props.salesInvoice?.originalInvoice?.id}
      />

      <SalesInvoicePositionsControls
        handleResetPositions={resetPositions}
        handleAddNewPosition={addNewPosition}
        handleDeletePositions={deletePositions}
        selected={select.selected}
        handleClonePosition={clonePosition}
        handleSave={handleSave}
        readOnly={props.readOnly}
        isLoading={isSaveLoading}
        hasLockedPositions={positions.some(p => p.isLocked)}
      />

      {showSaveErrors && (
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          onHide={() => setShowSaveErrors(false)}
        />
      )}
    </div>
  );
};

const validatePositions = async (
  positions: CreateUpdateSalesInvoicePositionDto[],
  isCorrection?: boolean,
): Promise<string[]> => {
  const validationErrors: string[] = [];
  for (let i = 0; i < positions.length; i++) {
    const validationResult = await validatePosition(positions[i], isCorrection);
    if (!validationResult.valid) {
      validationErrors.push(
        `Pozycja ${positions[i].order}: ${validationResult.errors.join(', ')}`,
      );
    }
  }
  return validationErrors;
};
